import { SftpImportConfigurationWorkflow, ImportType, ImportHierarchyOption, getSupplierColumnMappings, InventoryColumnMapping, SftpImportConfiguration, DataType } from '../../../store/Models/Api/SftpImportModels';
import IngredientsTableColumnManager, { InventoryColumnIds } from '../../../utilities/IngredientsTableColumn';
import { IStringLocalizer } from '../../../utilities/IStringLocalizer';
import { ToxnotServiceClient } from '../../../store/ToxnotServiceClient';

export interface ValidationResult {
    isValid: boolean;
    errorMessage?: string;
}

export class ColumnMappingHelper {
    /**
     * Get extensible columns from user's default BOM template
     */
    public static async getExtensibleColumns(): Promise<string[]> {
        try {
            const serviceClient = new ToxnotServiceClient();
            
            const result = await serviceClient.getDefaultBomColumnTemplate();
            if (result.result === "Ok" && result.data?.configuration?.extensibleColumns) {
                return result.data.configuration.extensibleColumns;
            }
            return [];
        } 
        catch (error) {
            return [];
        }
    }

    /**
     * Get extensible columns from user's default BOM template
     */
    public static async GetIfExternalDbIdRequired(): Promise<boolean> {
        try {
            const serviceClient = new ToxnotServiceClient();
            
            const result = await serviceClient.GetUserSettingsForTeamOwner();
            if (result.result === "Ok" && result.data?.importSettings?.requireExternalDbIdForImports) {
                return result.data.importSettings.requireExternalDbIdForImports;
            }
            return false;
        } 
        catch (error) {
            return false;
        }
    }

    /**
     * Get extensible columns from user's default BOM template
     */
    public static async GetConflictsForMapping(mappings: InventoryColumnMapping[], configId?: string): Promise<any> {
        try {
            const serviceClient = new ToxnotServiceClient();

            const result = await serviceClient.GetNewConfigColumnMappingConflictsDetermination(mappings, configId);
            if (result.result === "Ok" && result.data) {
                return { conflicts: result.data.conflicts as [], hasConflicts: result.data.hasConflicts };
            }
            // return { conflicts: '', hasConflicts: false };
            return { conflicts: [], hasConflicts: false };
        } 
        catch (error) {
            // return { conflicts: '', hasConflicts: false };
            return { conflicts: [], hasConflicts: false };
        }
    }

    /**
     * Generate mapping options based on import type
     * Note: This method is synchronous. For extensible columns, use getExtensibleColumns() separately.
     */
    public static getMappingOptions(
        configData: SftpImportConfigurationWorkflow,
        localizer: IStringLocalizer,
        extensibleColumns: string[] = []
    ): Array<{ value: string; label: string }> {
        let mappingOptions: Array<{ value: string; label: string }>;

        if (configData.importType === ImportType.Suppliers) {
            // Use supplier-specific column mappings
            const supplierColumnMappings = getSupplierColumnMappings(localizer);
            mappingOptions = supplierColumnMappings.map((mapping: { columnId: string; description: string }) => ({
                value: mapping.columnId,
                label: localizer.localizeString(mapping.description)
            }));
        } else {
            // Use inventory/BOM column mappings with proper localization
            const ingredientsManager = new IngredientsTableColumnManager();
            
            const friendlyNames = ingredientsManager.getIngredientsColumnFriendlyNames(localizer, extensibleColumns);

            mappingOptions = Object.keys(InventoryColumnIds).map(key => ({
                value: InventoryColumnIds[key as keyof typeof InventoryColumnIds],
                label: friendlyNames[InventoryColumnIds[key as keyof typeof InventoryColumnIds]] || key
            }));

            // Add extensible columns to mapping options
            extensibleColumns.forEach((columnName, index) => {
                mappingOptions.push({
                    value: `extensible-column-${index}`,
                    label: columnName
                });
            });
        }

        mappingOptions.unshift({ 
            value: '--tn-skip', 
            label: localizer.localizeString('Skip this column') 
        } as any);

        return mappingOptions;
    }

    // ===== VALIDATION METHODS =====

    /**
     * Validates that at least one column mapping is not skipped
     */
    public static validateAtLeastOneMapping(configData: SftpImportConfigurationWorkflow): ValidationResult {
        // Check if at least one mapping is not skipped
        const hasValidMapping = configData.spreadsheetHeadersToColumnIdMappings.some(
            mapping => mapping.columnId !== '--tn-skip' && mapping.columnId.trim() !== ''
        );

        return {
            isValid: hasValidMapping,
            errorMessage: hasValidMapping ? undefined : 'At least one column mapping must be provided to proceed.'
        };
    }

    /**
     * Validates that not all columns are configured to be skipped
     */
    public static validateNotAllSkipped(configData: SftpImportConfigurationWorkflow): ValidationResult {
        // Check if ALL columns are configured to be skipped
        const allMappings = configData.spreadsheetHeadersToColumnIdMappings;
        
        if (allMappings.length === 0) {
            return {
                isValid: false,
                errorMessage: 'No column mappings found.'
            };
        }
        
        // Return false if ALL mappings are skipped
        const allSkipped = allMappings.every(mapping => mapping.columnId === '--tn-skip');
        
        return {
            isValid: !allSkipped,
            errorMessage: allSkipped ? 'All columns are configured to be skipped. Please map at least one import column to a 3E Exchange column.' : undefined
        };
    }

    /**
     * Validates that not all columns are configured to be skipped
     */
    public static async validateExternalDbIdForMapping(mappings: InventoryColumnMapping[]): Promise<ValidationResult> {
        // Check if the external db id is req for the owner
        var res = await this.GetIfExternalDbIdRequired();
        
        const hasExternalDbIdMapping = mappings.some(mapping => 
            mapping.columnId === 'external-db-id'
        );
        
        if(!hasExternalDbIdMapping && res) {
            return {
                isValid: false,
                errorMessage: 'External DB ID is required for imports and must be included for all summary rows and linked material rows. Please adjust your column mapping to include External DB ID.'
            };
        }
        return {
            isValid: true
        };
    }

    /**
     * Validates that not all columns are configured to be skipped and provides user-friendly conflict messages
     */
    public static async validateNewMapping(
        configData: SftpImportConfigurationWorkflow,
        localizer: IStringLocalizer
    ): Promise<ValidationResult> {
        // Check if the external db id is req for the owner
        const extensibleColumns = await ColumnMappingHelper.getExtensibleColumns();
        var filteredMappings = configData.spreadsheetHeadersToColumnIdMappings.filter(m => m.columnId !== '--tn-skip');
        var res = await this.GetConflictsForMapping(filteredMappings, configData.configId);

        if(res.hasConflicts) {
            let conflictMessage = 'The column mapping in this configuration may conflict with an existing configuration. ' +
                'Do you want to proceed?';

            // If we have the required parameters, enhance the conflict message with friendly names
            const mappingOptions = this.getMappingOptions(configData, localizer, extensibleColumns);
            const mappingOptionsDict = mappingOptions.reduce<Record<string, string>>((dict, option) => {
                dict[option.value] = option.label;
                return dict;
            }, {});

            // Replace column IDs in conflict message with friendly names
            res.conflicts.forEach((option: { configurationName: string, headerName: string, columnId: string }) => {

                conflictMessage += `\n${option.configurationName}: ${option.headerName} = ${mappingOptionsDict[option.columnId]}`;
            });

            // }

            return {
                isValid: false,
                errorMessage: conflictMessage
            };
        }
        return {
            isValid: true
        };
    }

    /**
     * Validates Part Name requirement for hierarchy levels
     * Rule: All levels except the last level must have at least Part Name specified
     */
    public static validatePartNameRequirement(configData: SftpImportConfigurationWorkflow): ValidationResult {
        // This validation only applies when hierarchy is RepeatedMaterialIdentifiers
        const hasPartNameForLevel = configData.spreadsheetHeadersToColumnIdMappings.some(m => 
            m.columnId === 'part-name'
        );
        
        if (!hasPartNameForLevel) {
            return {
                isValid: false,
                errorMessage: 'Your SFTP configuration currently doesn\'t have a column for Part Name. Please include this column and try again.'
            };
        }

        // Get ALL mappings (including skipped ones) that have levels defined
        const allMappingsWithLevels = configData.spreadsheetHeadersToColumnIdMappings.filter(
            mapping => mapping.level !== null && mapping.level !== undefined
        );

        // Get all distinct levels (from ALL mappings, not just active ones)
        const levels = allMappingsWithLevels
            .map(m => m.level!)
            .filter((level, index, array) => array.indexOf(level) === index) // distinct
            .sort((a, b) => a - b); // sort numerically

        // For each level except the last one, check if at least one mapping has "part-name"
        for (let i = 0; i < levels.length - 1; ++i) {
            const level = levels[i];
            
            // Get all mappings for this level (including skipped ones)
            const mappingsForLevel = allMappingsWithLevels.filter(m => m.level === level);
            
            // Check if any mapping for this level is assigned to part-name
            const hasPartNameForLevel = mappingsForLevel.some(m => 
                m.columnId === 'part-name'
            );
            
            if (!hasPartNameForLevel) {
                return {
                    isValid: false,
                    errorMessage: 'All levels except the last level must have at least Part Name specified.'
                };
            }
        }

        return { isValid: true };
    }

    /**
     * Applies duplicate column-level validation logic
     * Auto-skips conflicting mappings (same column + same level)
     */
    public static applyDuplicateColumnLevelValidation(
        mappings: any[], 
        changedIndex: number
    ): any[] {

        const changedMapping = mappings[changedIndex];
        const changedColumnId = changedMapping.columnId;
        const changedLevel = changedMapping.level;
        
        // Skip validation for skipped columns or when using LevelColumnWithMultipleLevelsPerRow
        if (changedColumnId === '--tn-skip') {
            return mappings;
        }

        // Create a copy to avoid mutating the original array
        const updatedMappings = [...mappings];

        // Find and auto-skip conflicting mappings (same column + same level)
        updatedMappings.forEach((mapping, index) => {
            if (index !== changedIndex && 
                mapping.columnId === changedColumnId && 
                ((changedLevel === null && mapping.level === null) || 
                 (changedLevel !== null && mapping.level === changedLevel))) {
                
                // Auto-skip the conflicting mapping
                updatedMappings[index] = { ...mapping, columnId: '--tn-skip' };
            }
        });

        return updatedMappings;
    }

    /**
     * Runs all validations and returns the first error found, or success if all pass
     */
    public static validateAll(configData: SftpImportConfigurationWorkflow, shouldValidateBom: boolean): ValidationResult {
        // Run validations in order (matches original validation order)
        
        // 1. Check if all columns are skipped
        const notAllSkippedResult = this.validateNotAllSkipped(configData);
        if (!notAllSkippedResult.isValid) {
            return notAllSkippedResult;
        }
        
        // 2. Check if at least one column is mapped
        const atLeastOneMappingResult = this.validateAtLeastOneMapping(configData);
        if (!atLeastOneMappingResult.isValid) {
            return atLeastOneMappingResult;
        }

        if(shouldValidateBom) {
            // 3. Check Part Name requirement for hierarchy levels
            const partNameRequirementResult = this.validatePartNameRequirement(configData);
            if (!partNameRequirementResult.isValid) {
                return partNameRequirementResult;
            }
        }
        // All validations passed
        return { isValid: true };
    }

    /**
     * Determines if a mapping card should be visually inactive
     */
    public static isCardInactive(mapping: InventoryColumnMapping, importHierarchyOptionType: ImportHierarchyOption): boolean {
        const showHierarchy = importHierarchyOptionType === ImportHierarchyOption.RepeatedMaterialIdentifiers;
        
        if (!showHierarchy) {
            // If hierarchy is not enabled, card is inactive if no column is mapped (skipped)
            return mapping.columnId === '--tn-skip' || mapping.columnId.trim() === '';
        } else {
            // If hierarchy is enabled, card is inactive if no hierarchy level is mapped or column is skipped
            return mapping.columnId === '--tn-skip' || 
                   mapping.columnId.trim() === '' || 
                   mapping.level === null || 
                   mapping.level === undefined;
        }
    }

    /**
     * Find the best matching column ID for a given column name based on the predefined mapping dictionary
     * (based on NewProductViewController.ts logic)
     */
    public static findBestColumnMatch(
        columnName: string, 
        mappingOptions: Array<{ value: string; label: string }>,
        existingMappings?: string[],
        extensibleColumns?: string[]
    ): string | null {
        if (!columnName || !mappingOptions) return null;
        
        const normalizedColumnName = columnName.toLowerCase().trim();
        
        if (normalizedColumnName.length === 0) {
            return null;
        }

        // Skip the "Skip this column" option in matching
        const validMappingOptions = mappingOptions.filter(option => option.value !== '--tn-skip');
        
        // Get already used column IDs to avoid duplicates
        const usedColumnIds = existingMappings ? 
            existingMappings
                .filter(mapping => mapping !== '--tn-skip')
                .map(mapping => mapping) : 
            [];
        
        // First, try exact match using the predefined mapping dictionary (like NewProduct does)
        const preselectedMapping = this.getPreselectedMappingFromDictionary(normalizedColumnName, extensibleColumns);
        if (preselectedMapping && !usedColumnIds.includes(preselectedMapping)) {
            // Verify this mapping exists in the available options
            const matchingOption = validMappingOptions.find(option => option.value === preselectedMapping);
            if (matchingOption) {
                return preselectedMapping;
            }
        }
        
        // Fallback to fuzzy matching for cases not in the dictionary
        // Try exact match first (case insensitive)
        const exactMatch = validMappingOptions.find(option => 
            option.label.toLowerCase() === normalizedColumnName && 
            !usedColumnIds.includes(option.value)
        );
        if (exactMatch) return exactMatch.value;
        
        // Try partial match - check if column name contains or is contained in the description
        const partialMatch = validMappingOptions.find(option => {
            const normalizedLabel = option.label.toLowerCase();
            return !usedColumnIds.includes(option.value) && 
                   (normalizedLabel.includes(normalizedColumnName) || 
                    normalizedColumnName.includes(normalizedLabel));
        });
        if (partialMatch) return partialMatch.value;
        
        // No good match found, return null to default to skip
        return null;
    }

    /**
     * Get preselected mapping from static dictionary (based on NewProductViewController.ts implementation)
     */
    private static getPreselectedMappingFromDictionary(normalizedColumnName: string, extensibleColumns?: string[]): string | null {
        // This is based on the same mapping used in NewProductViewController.ts
        const preSelectionMap: { [key: string]: string } = {
            "chemical registry number": "casrn",
            "registry number": "casrn",
            "rn": "casrn",
            "cas": "casrn",
            "casrn": "casrn",
            "cas rn": "casrn",
            "cas number": "casrn",
            "cas#": "casrn",
            "cas #": "casrn",
            "cas num": "casrn",

            "ec number": "ec-number",
            "ec-number": "ec-number",
            "ec": "ec-number",
            "ecn": "ec-number",

            "chemical": "chemical-name",
            "chemical name": "chemical-name",
            "substance": "chemical-name",

            "description": "description",

            "level": "level",
            "lvl": "level",
            "lv": "level",

            "part name": "part-name",
            "part": "part-name",
            "component description": "part-name",

            "proprietary": "proprietary",
            "proprietary?": "proprietary",

            "supplier": "supplier",
            "supplier id": "user-facing-supplier-id",

            "external db id": "external-db-id",

            "uom": "unit-of-measure",
            "unit of measure": "unit-of-measure",
            "unit": "unit-of-measure",
            "units": "unit-of-measure",

            "weight": "weight",
            "yield": "yield",
            "material type": "material-type",
            "material family": "material-family",
            "nanomaterial": "nanomaterial",
            "nano": "nanomaterial",

            "note": "notes",
            "notes": "notes",

            "rc": "recycled-content",
            "recycled": "recycled-content",
            "recycled content": "recycled-content",

            "role": "role",

            "percent by weight": "percent-by-weight",
            "percent weight": "percent-by-weight",
            "% by weight": "percent-by-weight",
            "% weight": "percent-by-weight",
            "product %": "percent-by-weight",
            "product percent": "percent-by-weight",
            "product percent by weight": "percent-by-weight",
            "product percent weight": "percent-by-weight",
            "product % by weight": "percent-by-weight",
            "product % weight": "percent-by-weight",

            "component percent by weight": "component-percent-by-weight",
            "component percent weight": "component-percent-by-weight",
            "component % by weight": "component-percent-by-weight",
            "component % weight": "component-percent-by-weight",
            "material percent by weight": "component-percent-by-weight",
            "material percent weight": "component-percent-by-weight",
            "material % by weight": "component-percent-by-weight",
            "material % weight": "component-percent-by-weight",

            "facility": "plant",
            "facility name": "plant",
            "plant": "plant",
            "plant id": "plant",
            "plant name": "plant",

            "quantity": "quantity",
            "qty": "quantity",

            "unit weight": "unit-weight",

            "supplier part number": "supplier-part-number",
            "supplier part no": "supplier-part-number",
            "supplier part #": "supplier-part-number",
            "supplier_part#": "supplier-part-number",

            "product number": "product-number",
            "product no": "product-number",
            "product no.": "product-number",
            "product #": "product-number",
            "product#": "product-number",

            "physical state": "physical-state",
            "tag": "tags",
            "tags": "tags",
            "status": "status"
        };

        // Check static mappings first
        const staticMapping = preSelectionMap[normalizedColumnName];
        if (staticMapping) {
            return staticMapping;
        }

        // Check extensible columns (like NewProductViewController.ts does)
        if (extensibleColumns) {
            for (let i = 0; i < extensibleColumns.length; i++) {
                if (extensibleColumns[i].toLowerCase() === normalizedColumnName) {
                    return `extensible-column-${i}`;
                }
            }
        }

        return null;
    }
}
